import { useEffect } from 'react';
import { useUser, useAuth } from '@clerk/clerk-react';
import { useNavigate, useLocation } from 'react-router-dom';
import useHasEverAuthenticated from '../hooks/useHasEverAuthenticated';
import { buildTrackedAuthRedirectUrl } from '../analytics';
import { GAME_CONFIG } from '../games';

const REAUTH_MESSAGE = 'La tua sessione è scaduta. Accedi di nuovo per continuare a giocare con il tuo account.';
const isMondialiPublicPath = (pathname = '') =>
  pathname === '/mondiali' || (pathname.startsWith('/mondiali/') && pathname !== '/mondiali/registrazione');

const ReauthPrompt = () => {
  const { isSignedIn } = useUser();
  const { isLoaded: authLoaded } = useAuth();
  const hasEverAuthenticated = useHasEverAuthenticated();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!authLoaded) return;
    
    // Don't redirect if already on signin/signup pages
    if (/^\/sign(?:in|up)(?:\/|$)/.test(location.pathname)) return;
    if (isMondialiPublicPath(location.pathname)) return;
    // Puzzle pages offer an intentional sign-in decision after setup. Their route
    // wrapper enforces the server anonymous gameplay allowance.
    if (Object.values(GAME_CONFIG).some(({ pathName }) =>
      new RegExp(`^/${pathName}/\\d{4}-\\d{2}-\\d{2}/?$`).test(location.pathname))) return;
    
    let hasRedirected = false;
    try {
      hasRedirected = sessionStorage.getItem('reauth-prompt-shown') === 'true';
    } catch (_) {}
    
    if (!isSignedIn && hasEverAuthenticated && !hasRedirected) {
      try { sessionStorage.setItem('reauth-prompt-shown', 'true'); } catch (_) {}
      
      const currentUrl = (location.pathname || '/') + (location.search || '');
      const authUrl = buildTrackedAuthRedirectUrl({
        cta_kind: 'sign_in',
        source_surface: 'reauth_prompt',
        gate_reason: 'session_reauthentication_required',
        next_path: currentUrl,
        message: REAUTH_MESSAGE,
        is_returning_device: true,
        track_gate_viewed: true,
        trigger: 'system_reauth_redirect',
      });

      navigate(authUrl, { replace: true });
    }
  }, [authLoaded, isSignedIn, hasEverAuthenticated, navigate, location]);

  // This component no longer renders anything
  return null;
};

export default ReauthPrompt;
